import { css } from "@emotion/react";
import { Field } from "react-final-form";
import { useSelector } from "react-redux";

import { GreetingCard, Palette, TextBlock } from "@livechat/design-system-icons";
import { DesignToken, Icon, RadiusToken, SpacingToken, Text } from "@livechat/design-system-react-components";

import { AccordionItem } from "components/accordion-item/AccordionItem";
import { FinalFormSwitch } from "components/form/FinalFormSwitch";
import Block from "components/layout/Block";
import Flex from "components/layout/Flex";
import { isInIframe } from "helpers/app";
import { useToggle } from "hooks/use-toggle";
import { IAppearance } from "store/entities/application-settings/interfaces";
import {
  selectDefaultLogoUrl,
  selectDefaultVisualTemplateAppearance,
} from "store/entities/application-settings/selectors";
import { withVar } from "themes/helpers";

import { HideHelpDeskSwitch } from "./HideHelpDeskSwitch";
import { ColorPicker } from "./components/ColorPicker";
import { Content } from "./components/Content";
import { Footer } from "./components/Footer";
import { LogoSection } from "./components/LogoSection";
import { ShowPreviousMessagesSwitch } from "./components/ShowPreviousMessagesSwitch";

const colorPickerContainerStyles = css`
  padding: var(${SpacingToken.Spacing3}) 0px;
  border-radius: var(${RadiusToken.Radius3});
  border: 1px solid var(${DesignToken.BorderBasicPrimary});
`;

interface IProps {
  values: IAppearance;
  templateID: string;
  setTransactionID: (transactionId: string) => void;
}

// FIXME: Fix this the next time the file is edited.
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
function getSectionColors(sectionName: string, values: IAppearance) {
  return (
    Object.entries(values)
      // eslint-disable-next-line consistent-return, array-callback-return
      .map(([k, v]) => {
        if (k.startsWith(sectionName)) {
          return v?.value;
        }
      })
      .filter(Boolean)
  );
}

// FIXME: Fix this the next time the file is edited.
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export const VisualBuilderControls = ({ values, templateID, setTransactionID }: IProps) => {
  const [isPaletteOpen, togglePaletteOpen] = useToggle(false);
  const [isContentOpen, toggleContentOpen] = useToggle(false);
  const [isFooterOpen, toggleFooterOpen] = useToggle(false);
  const appearance = useSelector(selectDefaultVisualTemplateAppearance);
  const defaultLogoUrl = useSelector(selectDefaultLogoUrl);

  const backgroundColors = appearance !== null ? getSectionColors("appearanceBodyBgColor", appearance) : [];
  const textBoxColors = appearance !== null ? getSectionColors("appearanceContentBgColor", appearance) : [];
  const textColors = appearance !== null ? getSectionColors("appearanceTextColor", appearance) : [];
  const linkColors = appearance !== null ? getSectionColors("appearanceLinkColor", appearance) : [];
  const footerTextColors = appearance !== null ? getSectionColors("appearanceFooterTextColor", appearance) : [];
  const footerLinkColors = appearance !== null ? getSectionColors("appearanceFooterLinkColor", appearance) : [];
  const isEmbeddedInIframe = isInIframe();

  const COLOR_PICKER_OPTIONS = [
    {
      title: "Background color",
      id: 1,
      elements: [
        {
          id: 2,
          name: "appearance.appearanceBodyBgColor1.value",
          value: appearance?.appearanceBodyBgColor1?.value,
        },
        {
          id: 3,
          name: "appearance.appearanceBodyBgColor1.value",
          value: appearance?.appearanceBodyBgColor2?.value,
        },
        {
          id: 4,
          name: "appearance.appearanceBodyBgColor1.value",
          value: appearance?.appearanceBodyBgColor3.value,
        },
        {
          id: 5,
          name: "appearance.appearanceBodyBgColor1.value",
          value: appearance?.appearanceBodyBgColor4.value,
          invertCheckmark: true,
        },
        {
          name: "appearance.appearanceBodyBgColor1.value",
          id: 6,
          customColor: true,
          invertCheckmark: true,
          customColorChecked: !backgroundColors.includes(values?.appearanceBodyBgColor1?.value),
        },
      ],
    },
    {
      title: "Text box color",
      id: 7,
      elements: [
        {
          id: 8,
          name: "appearance.appearanceContentBgColor1.value",
          value: appearance?.appearanceContentBgColor1.value,
        },
        {
          id: 9,
          name: "appearance.appearanceContentBgColor1.value",
          value: appearance?.appearanceContentBgColor2.value,
        },
        {
          id: 10,
          name: "appearance.appearanceContentBgColor1.value",
          value: appearance?.appearanceContentBgColor3.value,
        },
        {
          id: 11,
          name: "appearance.appearanceContentBgColor1.value",
          value: appearance?.appearanceContentBgColor4.value,
          invertCheckmark: true,
        },
        {
          id: 12,
          name: "appearance.appearanceContentBgColor1.value",
          customColor: true,
          invertCheckmark: true,
          customColorChecked: !textBoxColors.includes(values?.appearanceContentBgColor1?.value),
        },
      ],
    },
    {
      title: "Text color",
      id: 13,
      elements: [
        {
          id: 14,
          name: "appearance.appearanceTextColor1.value",
          value: appearance?.appearanceTextColor1.value,
          invertCheckmark: true,
        },
        {
          id: 15,
          name: "appearance.appearanceTextColor1.value",
          value: appearance?.appearanceTextColor2.value,
          invertCheckmark: true,
        },
        {
          id: 16,
          name: "appearance.appearanceTextColor1.value",
          value: appearance?.appearanceTextColor3.value,
          invertCheckmark: true,
        },
        {
          id: 17,
          name: "appearance.appearanceTextColor1.value",
          value: appearance?.appearanceTextColor4.value,
        },
        {
          id: 18,
          name: "appearance.appearanceTextColor1.value",
          customColor: true,
          invertCheckmark: true,
          customColorChecked: !textColors.includes(values?.appearanceTextColor1?.value),
        },
      ],
    },
    {
      id: 19,
      title: "Link color",
      elements: [
        {
          id: 20,
          name: "appearance.appearanceLinkColor1.value",
          value: appearance?.appearanceLinkColor1.value,
          invertCheckmark: true,
        },
        {
          id: 21,
          name: "appearance.appearanceLinkColor1.value",
          value: appearance?.appearanceLinkColor2.value,
          invertCheckmark: true,
        },
        {
          id: 22,
          name: "appearance.appearanceLinkColor1.value",
          value: appearance?.appearanceLinkColor3.value,
          invertCheckmark: true,
        },
        {
          id: 23,
          name: "appearance.appearanceLinkColor1.value",
          value: appearance?.appearanceLinkColor4.value,
        },
        {
          id: 24,
          name: "appearance.appearanceLinkColor1.value",
          customColor: true,
          invertCheckmark: true,
          customColorChecked: !linkColors.includes(values?.appearanceLinkColor1?.value),
        },
      ],
    },
  ];

  const FOOTER_COLOR_PICKER_OPTIONS = [
    {
      title: "Text color",
      id: 1,
      elements: [
        {
          id: 2,
          name: "appearance.appearanceFooterTextColor1.value",
          value: appearance?.appearanceFooterTextColor1?.value,
          invertCheckmark: true,
        },
        {
          id: 3,
          name: "appearance.appearanceFooterTextColor1.value",
          value: appearance?.appearanceFooterTextColor2?.value,
          invertCheckmark: true,
        },
        {
          id: 4,
          name: "appearance.appearanceFooterTextColor1.value",
          value: appearance?.appearanceFooterTextColor3.value,
          invertCheckmark: true,
        },
        {
          id: 5,
          name: "appearance.appearanceFooterTextColor1.value",
          value: appearance?.appearanceFooterTextColor4.value,
        },
        {
          name: "appearance.appearanceFooterTextColor1.value",
          id: 6,
          customColor: true,
          customColorChecked: !footerTextColors.includes(values?.appearanceFooterTextColor1?.value),
        },
      ],
    },
    {
      title: "Link color",
      id: 7,
      elements: [
        {
          id: 8,
          name: "appearance.appearanceFooterLinkColor1.value",
          value: appearance?.appearanceFooterLinkColor1.value,
          invertCheckmark: true,
        },
        {
          id: 9,
          name: "appearance.appearanceFooterLinkColor1.value",
          value: appearance?.appearanceFooterLinkColor2.value,
          invertCheckmark: true,
        },
        {
          id: 10,
          name: "appearance.appearanceFooterLinkColor1.value",
          value: appearance?.appearanceFooterLinkColor3.value,
          invertCheckmark: true,
        },
        {
          id: 11,
          name: "appearance.appearanceFooterLinkColor1.value",
          value: appearance?.appearanceFooterLinkColor4.value,
        },
        {
          id: 12,
          name: "appearance.appearanceFooterLinkColor1.value",
          customColor: true,
          customColorChecked: !footerLinkColors.includes(values?.appearanceFooterLinkColor1?.value),
        },
      ],
    },
  ];

  return (
    <Flex
      column
      css={css`
        .visual-builder-control-item {
          margin: 6px 0;
        }
      `}
    >
      <AccordionItem
        className="visual-builder-control-item"
        opened={isPaletteOpen}
        title="Appearance"
        icon={<Icon source={Palette} />}
        onHeaderClick={togglePaletteOpen}
        fullWidth
      >
        {COLOR_PICKER_OPTIONS.map(({ title, elements, id }) => (
          <div key={id}>
            <Text>{title}</Text>
            <Flex css={colorPickerContainerStyles} marginTop="8px" marginBottom="24px">
              {elements.map(element => (
                <Field
                  key={element.id}
                  type="radio"
                  name={element.name}
                  value={element.value}
                  invertCheckmark={element.invertCheckmark}
                  customColor={element.customColor}
                  customColorChecked={element.customColorChecked}
                  component={ColorPicker as never}
                />
              ))}
            </Flex>
          </div>
        ))}
        <LogoSection
          logoUrl={defaultLogoUrl}
          showLogo={values?.appearanceShowLogo?.value as boolean}
          templateID={templateID}
          setTransactionID={setTransactionID}
        />
      </AccordionItem>
      <AccordionItem
        className="visual-builder-control-item"
        opened={isContentOpen}
        title="Content"
        icon={<Icon source={TextBlock} />}
        onHeaderClick={toggleContentOpen}
        fullWidth
      >
        <Flex column gap="24px">
          <Content />
          <Flex justifyContent="space-between" alignItems="center">
            <Text>
              Show <b>previous messages</b>
            </Text>
            <Field
              type="checkbox"
              name="appearance.contentShowPreviousMessages.value"
              component={ShowPreviousMessagesSwitch}
            />
          </Flex>
        </Flex>
      </AccordionItem>
      <AccordionItem
        className="visual-builder-control-item"
        opened={isFooterOpen}
        title="Footer"
        icon={<Icon source={GreetingCard} />}
        onHeaderClick={toggleFooterOpen}
        fullWidth
      >
        <Flex
          justifyContent="space-between"
          alignItems="center"
          marginBottom={isEmbeddedInIframe && !values.footerShowFooter.value ? "0px" : "16px"}
        >
          <Text>Show footer</Text>
          <Field type="checkbox" name="appearance.footerShowFooter.value" component={FinalFormSwitch} />
        </Flex>
        {values.footerShowFooter.value ? (
          <>
            {FOOTER_COLOR_PICKER_OPTIONS.map(({ title, elements, id }) => (
              <div key={id}>
                <Text>{title}</Text>
                <Flex css={colorPickerContainerStyles} marginTop="8px" marginBottom="12px">
                  {elements.map(element => (
                    <Field
                      key={element.id}
                      type="radio"
                      name={element.name}
                      value={element.value}
                      invertCheckmark={element.invertCheckmark}
                      customColor={element.customColor}
                      customColorChecked={element.customColorChecked}
                      component={ColorPicker as never}
                    />
                  ))}
                </Flex>
              </div>
            ))}
            <Text>Content</Text>
            <Block marginTop="8px" />
            <Footer />
          </>
        ) : null}
        {!isEmbeddedInIframe && (
          <hr
            css={css({
              background: withVar(DesignToken.BorderBasicPrimary),
              marginTop: "16px",
              marginBottom: "16px",
              width: "100%",
              height: "1px",
              border: 0,
            })}
          />
        )}

        <HideHelpDeskSwitch isVisualBuilder />
      </AccordionItem>
    </Flex>
  );
};
